import { type Calibration } from "@/editor-core/document"
import { netPolygon } from "@/editor-core/geometry"
import {
  type ProductAssignment,
  type SeamSettings,
  assignCuts,
  deriveCuts,
  rollGood,
  seamMetrics,
  orderSf as orderSfFor,
} from "@/editor-core/seams"
import type { CutPlans, WasteMode } from "@/editor-core/document"
import type { Room } from "@/stores/takeoff-store"

export type ComputedRoom = Room & {
  sqft: number | null
  perimFt: number | null
  order: number | null
  /** Which model produced `order` — surfaced so mixed rollups stay honest. */
  mode: WasteMode
  /** Seam-derived, this room only (multiplier included): linear feet of its
   *  cuts and the waste the layout itself produces. Null without a plan. */
  lf: number | null
  cutCount: number | null
  computedWasteSf: number | null
  /** Both spellings of the layout's waste — "+% of net" and the gross
   *  fraction an ex-Measure estimator expects. */
  wastePctOfNet: number | null
  wastePctOfGross: number | null
}

function computeRoom(
  room: Room,
  ftPerPx: number | null,
  settings: SeamSettings,
  wasteModeDefault: WasteMode
): ComputedRoom {
  const net = netPolygon(room.outline, room.holes)
  const sqft = ftPerPx ? net.areaPx2 * ftPerPx * ftPerPx : null
  const perimFt = ftPerPx ? net.outerPerimPx * ftPerPx : null

  // Per-room seam quantities from a SOLO nesting of the room's own cuts:
  // narrow fills pack side-by-side across the roll width (three 3'×10'
  // fills consume ~10' of roll, not 30'), so "Order from cut plan" reflects
  // what the room actually needs. The rollup and the cut tray use the
  // product-level allocation instead, where cross-room pooling can do even
  // better — that difference is real and visible, not a bug.
  let lf: number | null = null
  let cutCount: number | null = null
  let computedWasteSf: number | null = null
  let wastePctOfNet: number | null = null
  let wastePctOfGross: number | null = null
  if (room.seams && room.product && rollGood(room.product) && ftPerPx && sqft != null) {
    const cuts = deriveCuts(
      { id: room.id, name: room.name, outline: room.outline, holes: room.holes, multiplier: room.multiplier, seams: room.seams },
      ftPerPx,
      settings
    )
    const solo = assignCuts(cuts, undefined, room.product, settings)
    lf = solo.totalLf
    cutCount = cuts.length
    const metrics = seamMetrics(lf, cuts.length, room.product, sqft * Math.max(room.multiplier, 1))
    computedWasteSf = metrics.computedWasteSf
    wastePctOfNet = metrics.wastePctOfNet
    wastePctOfGross = metrics.wastePctOfGross
  }

  // Roll goods with a seam plan default to the computed model; everything
  // else to the project default. An explicit per-room choice is sticky and
  // always wins (Measure's reverts silently — ours must not).
  const mode =
    room.wasteMode ?? (computedWasteSf != null ? "computed" : wasteModeDefault)

  return {
    ...room,
    sqft,
    perimFt,
    mode,
    lf,
    cutCount,
    computedWasteSf,
    wastePctOfNet,
    wastePctOfGross,
    order: orderSfFor(sqft, mode, computedWasteSf, room.waste, room.multiplier),
  }
}

/** Quantities are always derived from the drawing — never stored or typed. */
export function computeRooms(
  rooms: Room[],
  ftPerPx: number | null,
  settings: SeamSettings,
  wasteModeDefault: WasteMode = "net"
): ComputedRoom[] {
  return rooms.map((room) => computeRoom(room, ftPerPx, settings, wasteModeDefault))
}

/**
 * All rooms across every sheet, each measured with its OWN sheet's
 * calibration — the per-product rollup is takeoff-wide (you order once per
 * product, not per sheet). Rooms on uncalibrated sheets come back with null
 * quantities; the rollup surfaces them as "unmeasured" rather than silently
 * dropping square footage.
 */
export function computeAllRooms(
  rooms: Room[],
  calibBySheet: Record<number, Calibration>,
  settings: SeamSettings,
  wasteModeDefault: WasteMode = "net"
): ComputedRoom[] {
  return rooms.map((room) =>
    computeRoom(room, calibBySheet[room.sheetId]?.ftPerPx ?? null, settings, wasteModeDefault)
  )
}

/**
 * The takeoff-wide roll nesting, per product: every seamed roll-goods room's
 * cuts (multiplied rooms pooled) first-fit onto shared rolls around the
 * human's pins. This is what the cut sheet renders and what the rollup's
 * LF / roll counts come from.
 */
export function rollAssignments(
  rooms: Room[],
  calibBySheet: Record<number, Calibration>,
  cutPlans: CutPlans,
  settings: SeamSettings
): Record<string, ProductAssignment> {
  const byProduct = new Map<string, { product: NonNullable<Room["product"]>; cuts: ReturnType<typeof deriveCuts> }>()
  for (const room of rooms) {
    if (!room.seams || !room.product || !rollGood(room.product)) continue
    const ftPerPx = calibBySheet[room.sheetId]?.ftPerPx
    if (!ftPerPx) continue
    const cuts = deriveCuts(
      { id: room.id, name: room.name, outline: room.outline, holes: room.holes, multiplier: room.multiplier, seams: room.seams },
      ftPerPx,
      settings
    )
    const entry = byProduct.get(room.product.id)
    if (entry) entry.cuts.push(...cuts)
    else byProduct.set(room.product.id, { product: room.product, cuts: [...cuts] })
  }
  const out: Record<string, ProductAssignment> = {}
  for (const [productId, { product, cuts }] of byProduct) {
    out[productId] = assignCuts(cuts, cutPlans[productId], product, settings)
  }
  return out
}
